import * as React from "react";
import { PaymentCountry, PaymentState, CreditCard, StoredCreditCard } from "../types";
import { getValidator } from "../topup/credit-card-validator";
import { Form, FormRef } from "@ajayvikas/react-fluent-validation";
import { MaskedInput } from "../Controls/MaskedInput";
import { Input } from "../Controls/Input";
import { Select } from "../Controls/Select";
import t from "../i18n";
import { dialogService } from "react-api-dialog";
import { CreditCardImage } from "./CreditCardImage";

const ccMask = [/\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/];
const mmyyMask = [/\d/, /\d/, ' ', '/', ' ', /\d/, /\d/];
const cvvMask = [/\d/, /\d/, /\d/, /\d/];

declare type CreditCardProps = {
    countries: PaymentCountry[];
    addressSettings: { usUkCa: boolean, others: boolean };
    creditCard: CreditCard | undefined;
    creditCardChanged: (creditCard: CreditCard) => void;
    showStoredCard: boolean;
    storedCreditCards: StoredCreditCard[]
}
export default class CreditCardComp extends React.Component<CreditCardProps> {
    _creditCardForm: FormRef | null;
    validate() {
        if (!this._creditCardForm) return false;
        const validationResult = this._creditCardForm.submit();
        const isValid = validationResult?.isValid() ?? false;
        return isValid;
    }
    creditCardChanged = (value: any, propertyName: keyof CreditCard) => {
        const creditCard = this.props.creditCard;
        if (!creditCard) return;
        this.props.creditCardChanged({ ...creditCard, [propertyName]: value });
    }
    showFindCreditCardNumber = () => {
        dialogService.showComponent(CreditCardImage, {
            hash: "tooltip", animation: "fade", width: "100%", style: {
                width: "100%",
                height: "100%",
                background: "transparent",
                // display: "flex",
                // justifyContent: "center",
                // alignItems: "center"
            },
            showCloseButton: true
        });
    }
    pipe = (conformedValue: string, config: { rawValue: string }) => {
        const [mm, yy] = config.rawValue.replace(/ /g, "").split("/");
        if (!yy) return conformedValue;
        if (yy.length !== 4) return conformedValue;
        return `${mm} / ${yy.substr(2)}`;
    }
    render() {
        const creditCard = this.props.creditCard || {} as CreditCard;
        const selectedCountry = this.props.countries.find(c => c.countryCode === creditCard!.country);
        const isUsUkCa = selectedCountry ? ["US", "GB", "CA"].indexOf(selectedCountry.countryCode) > -1 : false;
        const addressSettings = this.props.addressSettings;
        const renderStreetAddress = isUsUkCa ? addressSettings.usUkCa : addressSettings.others;
        const renderCity = isUsUkCa ? addressSettings.usUkCa : addressSettings.others;
        const renderState = isUsUkCa ? addressSettings.usUkCa : false;
        const renderZip = isUsUkCa ? addressSettings.usUkCa : false;
        const creditCardValidator = getValidator(selectedCountry, addressSettings)
        let states: PaymentState[] = [];
        if (creditCard && renderState) {
            if (selectedCountry) {
                states = selectedCountry.states || [];
            }
        }
        const radiusZero = this.props.storedCreditCards && this.props.storedCreditCards.length > 0
        const style = radiusZero ? { paddingTop: 15, borderTopRightRadius: 0 } : { paddingTop: 15 };
        return (
            <Form ref={f => this._creditCardForm = f} className="block new-credit-card" style={style}
                object={creditCard}
                validator={creditCardValidator}>
                <div className="row">
                    <div className="col-sm-12">
                        <div className="form-group relative">
                            <h5>{t.topup("credit_card_number")}</h5>
                            <MaskedInput autoComplete="cc-number"
                                className="form-control" propertyName="creditCardNumber"
                                maxLength={19} mask={ccMask} guide={false}
                                value={creditCard.creditCardNumber || ""}
                                onChange={e => this.creditCardChanged(e.target.value, "creditCardNumber")} />
                        </div>
                    </div>
                    <div className="col-sm-6">
                        <div className="form-group relative">
                            <h5>{t.topup("expiration_date_placeholder")}</h5>
                            <MaskedInput autoComplete="cc-exp" mask={mmyyMask} guide={false}
                                maxLength={7} propertyName="expiryMonthyear"
                                placeholder="MM / YY" className="form-control" value={creditCard.expiryMonthyear || ""}
                                onChange={e => this.creditCardChanged(e.target.value, "expiryMonthyear")}
                                pipe={this.pipe}
                            />
                        </div>
                    </div>
                    <div className="col-sm-6">
                        <div className="form-group relative">
                            <h5>{t.topup("cvv_placeholder")}</h5>
                            <MaskedInput autoComplete="cc-csc" mask={cvvMask} guide={false} maxLength={16}
                                propertyName="cvvCode" className="form-control" placeholder="CVV"
                                value={creditCard.cvvCode || ""} onChange={e => this.creditCardChanged(e.target.value, "cvvCode")}
                            />
                        </div>
                    </div>
                    <div className="col-sm-12">
                        <div className="form-group input-image-group relative">
                            <h5>{t.topup("full_name_placeholder")}</h5>
                            <Input autoComplete="cc-name" type="text" maxLength={50} className="form-control" propertyName="lastName"
                                value={creditCard.lastName || ""} onChange={e => this.creditCardChanged(e.target.value, "lastName")} />
                            <img src="/images/veon/Person Icon.png" style={{ top: 30 }} />
                        </div>
                    </div>
                    <div className="col-sm-12 text-right">
                        <a href="javascript:void(0)" onClick={this.showFindCreditCardNumber} className="text-right find-card-num-tip">
                            <h5 style={{ display: 'inline' }}>{t.topup("find_credit_card_number")}</h5>
                        </a>
                    </div>
                    <div className="col-sm-12">
                        <hr />
                        <h5 className="form-group" style={{ fontWeight: 700 }}>{t.topup("billing_address_placeholder")}</h5>
                    </div>
                    {renderStreetAddress && <div className="col-sm-6">
                        <div className="form-group input-image-group relative">
                            <h5>{t.topup("street_address_placeholder")}</h5>
                            <Input autoComplete="billing street-address" type="text"
                                maxLength={50} className="form-control" propertyName="streetAddress"
                                value={creditCard.streetAddress || ""} onChange={e => this.creditCardChanged(e.target.value, "streetAddress")} />
                            <img src="/images/veon/Adress Sign Icon.png" />
                        </div>
                    </div>}
                    {renderCity && <div className="col-sm-6">
                        <div className="form-group input-image-group relative">
                            <h5>{t.topup("city_placehoder")}</h5>
                            <Input autoComplete="billing locality" type="text" maxLength={50}
                                className="form-control" propertyName="city" value={creditCard.city || ""}
                                onChange={e => this.creditCardChanged(e.target.value, "city")} />
                            <img src="/images/veon/Building Icon.png" />
                        </div>
                    </div>}
                    <div className="col-sm-12">
                        <div className="form-group">
                            <h5>{t.topup("country_placeholder")}</h5>
                            <Select autoComplete="billing country" className="form-control" value={creditCard.country} propertyName="country"
                                onChange={e => this.creditCardChanged(e.target.value, "country")}>
                                {this.props.countries.map(c => <option key={c.countryCode} value={c.countryCode}>{c.countryName}</option>)}
                            </Select>
                        </div>
                    </div>
                    {renderState && <div className="col-sm-6">
                        <div className="form-group relative">
                            <h5>{t.topup("state_placeholder")}</h5>
                            {states.length > 0 &&
                                <Select autoComplete="billing region" className="form-control" value={creditCard.state} propertyName="state"
                                    onChange={e => this.creditCardChanged(e.target.value, "state")}>
                                    <option key="ss" value="">[{t.common("select_state")}]</option>
                                    {states.map(s => <option key={s.stateCode} value={s.stateCode}>{s.stateName}</option>)}
                                </Select>}
                            {states.length === 0 && <Input autoComplete="billing region" type="text" maxLength={50}
                                className="form-control" value={creditCard.state} propertyName="state"
                                onChange={e => this.creditCardChanged(e.target.value, "state")}
                            />}
                        </div>
                    </div>}
                    {renderZip && <div className="col-sm-6">
                        <div className="form-group relative">
                            <h5>{t.topup("postal_code_placeholder")}</h5>
                            <Input autoComplete="billing postal-code" type="text" maxLength={10} className="form-control"
                                propertyName="zipCode" value={creditCard.zipCode || ""}
                                onChange={e => this.creditCardChanged(e.target.value, "zipCode")} />
                        </div>
                    </div>}
                    {this.props.showStoredCard && <div className="col-sm-12">
                        <div className="form-group">
                            <input style={{ marginLeft: 2 }} type="checkbox" checked={creditCard.storeCc || false}
                                onChange={e => this.creditCardChanged(e.target.checked, "storeCc")} />
                            <label className="ml-1">{t.topup("stored_cc_future")}</label>
                        </div>
                    </div>}
                </div>
            </Form>
        );
    }
}